import { IMGPROXY_URL_REGEXP, decodeProxyUrl, MEDIA_DOMAIN_REGEXP } from '@/lib/url'
import { useState, useEffect, useRef, memo, useCallback, useMemo } from 'react'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $getNodeByKey } from 'lexical'
import { UNKNOWN_LINK_REL } from '@/lib/constants'
import { useEditableCarousel } from '@/components/carousel'
import { useMe } from '@/components/me'
import { processSrcSetInitial } from '@/lib/lexical/exts/item-context'
import FileError from '@/svgs/editor/file-error.svg'
import preserveScroll from '@/components/preserve-scroll'
import { $replaceNodeWithLink } from '@/lib/lexical/nodes/utils'
import { useLexicalEditable } from '@lexical/react/useLexicalEditable'
import useDecoratorNodeSelection from '@/components/editor/hooks/use-decorator-selection'

function LinkRaw ({ className, children, src, rel }) {
  const isRawURL = /^https?:\/\//.test(children?.[0])
  return (
    // eslint-disable-next-line
    <a
      className={className}
      target='_blank'
      rel={rel ?? UNKNOWN_LINK_REL}
      href={src}
    >{isRawURL || !children ? src : children}
    </a>
  )
}

function MediaLoading ({ autolink = false }) {
  const style = autolink ? { width: '200px', height: '150px' } : undefined
  return <div className='sn-media__loading' style={style} />
}

function MediaError ({ className, width, height, src, rel }) {
  return (
    <LinkRaw className={className} rel={rel} src={src}>
      {width && height && (
        <div className='sn-media__error' title={src}>
          <FileError />
          <p>
            content not available
            <br />
            <span className='fw-medium'>
              {src}
            </span>
          </p>
        </div>
      )}
    </LinkRaw>
  )
}

const Media = memo(function Media ({
  src, bestResSrc, srcSet, sizes, width, alt, title,
  height, onClick, onError, video, onLoad, isLoading, className = '', mediaRef
}) {
  const sized = !!(width && height && width > 0 && height > 0)

  // Hide while loading: use opacity: 0 but keep visible dimensions
  // iOS 18 won't fire onload for images with zero dimensions + loading='lazy'
  // Using opacity keeps the image "visible" to the browser so it loads properly
  const hiddenStyle = isLoading
    ? { opacity: 0, position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }
    : undefined

  return (
    <>
      {video
        ? (
          <video
            className={`sn-media__video${sized ? ' sn-media__video--sized' : ''} ${className}`}
            src={src}
            preload={bestResSrc !== src ? 'metadata' : undefined}
            controls
            poster={bestResSrc !== src ? bestResSrc : undefined}
            width={width}
            height={height}
            onError={onError}
            onLoadedMetadata={onLoad}
            style={hiddenStyle}
            ref={mediaRef}
          />
          )
        : (
          <img
            className={`sn-media__img${sized ? ' sn-media__img--sized' : ''} ${className}`}
            src={src}
            alt={alt}
            title={title}
            srcSet={srcSet}
            sizes={sizes}
            width={width}
            height={height}
            loading='lazy'
            decoding='async'
            onClick={onClick}
            onError={onError}
            onLoad={onLoad}
            style={hiddenStyle}
            ref={mediaRef}
          />
          )}
    </>
  )
})

/**
 * wrapper component that handles media rendering

 * @param {string} props.src - media source url
 * @param {string} props.srcSet - media source set string from imgproxy and ItemContextExtension
 * @param {string} props.bestResSrc - media best resolution source url from imgproxy and ItemContextExtension
 * @param {number} props.width - media width
 * @param {number} props.height - media height
 * @param {string} props.alt - media alt text
 * @param {string} props.title - media title
 * @param {string} props.status - media status (error, pending, etc.)
 * @param {string} props.kind - media kind (image, video)
 * @param {number} props.maxWidth - media max width
 * @returns {JSX.Element} media or link component
 */
export default function MediaComponent ({ src, srcSet, bestResSrc, width, height, alt, title, kind: initialKind, linkFallback = true, nodeKey }) {
  const [editor] = useLexicalComposerContext()
  const editable = useLexicalEditable()
  const mediaRef = useRef(null)
  const [kind, setKind] = useState()

  useDecoratorNodeSelection(nodeKey, {
    ref: mediaRef,
    deletable: false,
    focusedClass: 'focused'
  })

  const url = IMGPROXY_URL_REGEXP.test(src) ? decodeProxyUrl(src) : src

  // TODO: basically an hack, Lexical could handle this via MediaCheckExtension
  // we're profiting from the fact that MediaOrLink actually does a media check
  // if the media turned out to be a link, replace the media node with a link node
  const $replaceWithLink = useCallback(() => {
    const node = $getNodeByKey(nodeKey)
    if (node) {
      $replaceNodeWithLink(node, url)
    }
  }, [url, nodeKey])

  const $confirmMedia = useCallback(() => {
    const node = $getNodeByKey(nodeKey)
    if (!node) return

    if (kind === 'image' || kind === 'video') {
      node.setKind(kind)
      node.setStatus('done')
    }
  }, [kind, nodeKey])

  useEffect(() => {
    editor.update(() => {
      if (kind === 'unknown' || kind === 'disabled') {
        $replaceWithLink()
      }
      if (kind === 'image' || kind === 'video') {
        $confirmMedia()
      }
    })
  }, [kind, $replaceWithLink, $confirmMedia, editor])

  return (
    <MediaOrLink
      src={src}
      srcSet={srcSet}
      bestResSrc={bestResSrc}
      width={width}
      height={height}
      alt={alt}
      title={title}
      kind={initialKind}
      linkFallback={linkFallback}
      setKind={setKind}
      editable={editable}
      mediaRef={mediaRef}
    />
  )
}

const PROXY_TIMEOUT_MS = 5000

/**
 * When the imgproxy fails (e.g. on GIFs or flaky responses), try loading the
 * original URL directly instead of showing the error placeholder.
 *
 * Also times out slow proxy loads — if the proxy hasn't responded within
 * PROXY_TIMEOUT_MS, the fallback is triggered automatically.
 *
 * Returns overridden src/srcSet/sizes/bestResSrc with the fallback applied,
 * an `onProxyError` callback that returns true if a fallback was triggered,
 * and a `cancelTimeout` to clear the proxy timer on successful load.
 */
function useProxyFallback (media, editable) {
  const { me } = useMe()
  const [fallbackSrc, setFallbackSrc] = useState(null)
  const { addMedia, confirmMedia, removeMedia } = useEditableCarousel(editable)
  const timeoutRef = useRef(null)

  const canFallback = useMemo(() => {
    if (fallbackSrc) return false // already falling back
    // only relevant when we're loading through the proxy
    const isProxied = media.srcSet || media.originalSrc !== media.src
    if (!isProxied) return false
    // self-hosted images can always fall back safely
    if (MEDIA_DOMAIN_REGEXP.test(media.originalSrc)) return true
    // external images: respect imgproxyOnly preference
    return !me?.privates?.imgproxyOnly
  }, [fallbackSrc, media.srcSet, media.originalSrc, media.src, me?.privates?.imgproxyOnly])

  // time out slow proxy loads and trigger fallback automatically
  useEffect(() => {
    clearTimeout(timeoutRef.current)
    const isProxied = media.srcSet || media.originalSrc !== media.src
    if (!isProxied || fallbackSrc) return

    timeoutRef.current = setTimeout(() => {
      if (canFallback) {
        setFallbackSrc(media.originalSrc)
      }
    }, PROXY_TIMEOUT_MS)

    return () => clearTimeout(timeoutRef.current)
  }, [media.src, media.srcSet, media.originalSrc, fallbackSrc, canFallback])

  // when falling back, update carousel to use the original URL
  useEffect(() => {
    if (!fallbackSrc) return
    removeMedia(media.bestResSrc)
    addMedia({ src: fallbackSrc, originalSrc: media.originalSrc, rel: UNKNOWN_LINK_REL })
    confirmMedia(fallbackSrc)
  }, [fallbackSrc, media.bestResSrc, media.originalSrc, addMedia, confirmMedia, removeMedia])

  const onProxyError = useCallback(() => {
    clearTimeout(timeoutRef.current)
    if (canFallback) {
      setFallbackSrc(media.originalSrc)
      return true
    }
    return false
  }, [canFallback, media.originalSrc])

  const cancelTimeout = useCallback(() => {
    clearTimeout(timeoutRef.current)
  }, [])

  return {
    src: fallbackSrc || media.src,
    srcSet: fallbackSrc ? undefined : media.srcSet,
    sizes: fallbackSrc ? undefined : media.sizes,
    bestResSrc: fallbackSrc || media.bestResSrc,
    onProxyError,
    cancelTimeout
  }
}

export function MediaOrLink ({ linkFallback = true, editable, mediaRef, ...props }) {
  const media = useMediaHelper({ ...props, editable })
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(false)
  const { showCarousel, addMedia, confirmMedia, removeMedia } = useEditableCarousel(editable)
  const { src, srcSet, sizes, bestResSrc, onProxyError, cancelTimeout } = useProxyFallback(media, editable)

  useEffect(() => {
    if (!media.bestResSrc) return
    addMedia({ src: media.bestResSrc, originalSrc: media.originalSrc, rel: UNKNOWN_LINK_REL })
  }, [addMedia, media.bestResSrc, media.originalSrc])

  useEffect(() => {
    if (!media.image) return
    confirmMedia(media.bestResSrc)
  }, [confirmMedia, media.image, media.bestResSrc])

  const handleClick = useCallback(() => showCarousel({ src: bestResSrc }),
    [showCarousel, bestResSrc])

  const handleError = useCallback((err) => {
    // onError hands us a bare Event; the MediaError on the target element names
    // the real cause (network vs decode vs unsupported src)
    console.error('Error loading media', {
      src: err?.target?.currentSrc || err?.target?.src,
      code: err?.target?.error?.code,
      message: err?.target?.error?.message
    })
    if (onProxyError()) return
    removeMedia(bestResSrc)
    setError(true)
    setIsLoading(false)
  }, [onProxyError, removeMedia, bestResSrc])

  const handleLoad = useCallback(() => {
    cancelTimeout()
    setIsLoading(false)
  }, [cancelTimeout])

  if (!media.src) return null

  if (!error) {
    if (media.image || media.video) {
      // when we don't know the dimensions of the media (e.g. autolink),
      // preserveScroll helps us avoid scrolling shift when media finally loads
      const content = (
        <>
          {isLoading && <MediaLoading autolink={props.kind === 'unknown'} />}
          <Media
            {...media}
            src={src}
            srcSet={srcSet}
            sizes={sizes}
            bestResSrc={bestResSrc}
            onClick={handleClick}
            onError={handleError}
            onLoad={handleLoad}
            isLoading={isLoading}
            mediaRef={mediaRef}
          />
        </>
      )

      // ItemEmbed doesn't create a container for us, in that case we wrap the content in the sn-media span
      return preserveScroll(() => media?.style ? <span className='sn-media' style={media.style}>{content}</span> : content)
    }
  }

  if (linkFallback) {
    if (media.image || media.video) {
      return <MediaError width={media.width} height={media.height} src={media.src} rel={UNKNOWN_LINK_REL} />
    } else {
      return <LinkRaw className='sn-media-autolink__loading' src={media.src} rel={UNKNOWN_LINK_REL} />
    }
  }

  return null
}

// determines how the media should be displayed given the params, me settings, and editor tab
export const useMediaHelper = ({ src, srcSet, srcSetIntital, bestResSrc, width, height, kind, alt, title, topLevel, setKind, editable }) => {
  const { me } = useMe()
  const trusted = useMemo(() => !!(srcSet || srcSetIntital) || IMGPROXY_URL_REGEXP.test(src) || MEDIA_DOMAIN_REGEXP.test(src), [srcSet, srcSetIntital, src])
  // backwards compatibility: legacy srcSet handling
  const legacySrcSet = useMemo(() => processSrcSetInitial(srcSetIntital, src), [srcSetIntital, src])
  const [isImage, setIsImage] = useState((kind === 'image' || legacySrcSet?.video === false) && trusted)
  const [isVideo, setIsVideo] = useState(kind === 'video' || legacySrcSet?.video)
  const showMedia = useMemo(() => editable || me?.privates?.showImagesAndVideos !== false, [me?.privates?.showImagesAndVideos, editable])

  useEffect(() => {
    // don't load media if user has disabled them
    // or if the user has disabled non-proxied media and media is not proxied or a video (can't proxy videos)
    if (!showMedia || (me?.privates?.imgproxyOnly && (!trusted || isVideo))) {
      setKind?.('disabled')
      return
    }
    if (isVideo || isImage) return

    const controller = new AbortController()

    const checkMedia = async () => {
      try {
        const res = await fetch(`/api/media-check?url=${encodeURIComponent(src)}`, { signal: controller.signal })
        if (!res.ok) return

        const data = await res.json()

        if (data.isVideo) {
          setIsVideo(true)
          setIsImage(false)
          setKind?.('video')
        } else if (data.isImage) {
          setIsImage(true)
          setKind?.('image')
        } else {
          setKind?.('unknown')
        }
      } catch (error) {
        if (error.name === 'AbortError') return
        console.warn('cannot check media type', error)
      }
    }
    checkMedia()

    return () => {
      // abort the fetch
      try { controller.abort() } catch {}
    }
  }, [src, setIsImage, setIsVideo, showMedia, setKind, trusted, isVideo])

  let style = null
  if (legacySrcSet?.srcSet) {
    srcSet = legacySrcSet?.srcSet
    bestResSrc = legacySrcSet?.bestResSrc
    width = legacySrcSet?.width
    height = legacySrcSet?.height
    if (width && height && width > 0 && height > 0) {
      style = {
        '--height': `${height}`,
        '--width': `${width}`
      }
    }
  }

  const sizes = useMemo(() => srcSet ? '66vw' : undefined, [srcSet])

  return {
    src,
    srcSet,
    alt,
    title,
    originalSrc: IMGPROXY_URL_REGEXP.test(src) ? decodeProxyUrl(src) : src,
    sizes,
    bestResSrc,
    style,
    width,
    height,
    image: (!me?.privates?.imgproxyOnly || trusted) && showMedia && isImage && !isVideo,
    video: !me?.privates?.imgproxyOnly && showMedia && isVideo
  }
}
